import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Modal } from '@els/els-react--modal';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { STEPS, remoteAccessDialogTitles } from './RemoteAccessDialogConstants';
import { Header as StepChoiceHeader, Content as StepChoiceContent } from './Components/StepChoice';
import {
  Header as StepActivationHeader,
  Content as StepActivationContent,
  Footer as StepActivationFooter,
} from './Components/StepActivation';

import './RemoteAccessDialog.scss';

// Each step renders its own Modal instance (own id + accessible name) instead of swapping the
// content of a single long-lived Modal. Remounting re-runs the Leyden focus trap, so focus moves
// into the new dialog and screen readers announce the new dialog name (WCAG 2.4.3 / 4.1.2).
const MODALS = {
  CHOICE: {
    id: 'remote-access-modal',
    label: remoteAccessDialogTitles.CHOICE,
    // Reserves height for the spinner -> department list swap, see RemoteAccessDialog.scss
    modifier: 'remote-access-dialog--choice',
  },
  ACTIVATION: {
    id: 'remote-access-activation-modal',
    label: remoteAccessDialogTitles.ACTIVATION,
    // One action only, so focus goes straight to "Got it" instead of the dialog window — its
    // aria-describedby points to the visible confirmation text for screen readers.
    shouldFocusPrimaryAction: true,
  },
};

// `step` is the dispatch key; `props` is what each step's components render from
function getStepConfig(step, props) {
  switch (step) {
    case STEPS.INITIAL: {
      return {
        modal: MODALS.CHOICE,
        header: <StepChoiceHeader {...props} />,
        content: <SimpleSpinner className="remote-access-dialog__spinner" />,
        footer: null,
      };
    }
    case STEPS.CHOICE: {
      return {
        modal: MODALS.CHOICE,
        header: <StepChoiceHeader {...props} />,
        content: <StepChoiceContent {...props} />,
        footer: null,
      };
    }
    case STEPS.ACTIVATION: {
      return {
        modal: MODALS.ACTIVATION,
        header: <StepActivationHeader {...props} />,
        content: <StepActivationContent {...props} />,
        footer: <StepActivationFooter {...props} />,
      };
    }
    default: {
      Logger.error('RemoteAccessDialog - invalid step');

      return null;
    }
  }
}

function RemoteAccessDialog(props) {
  const { step, email, fetchDepartments, onClose } = props;
  const confirmButtonRef = useRef(null);
  const primaryActionFocusTrapProps = useMemo(() => ({ focusTrapOptions: { initialFocus: () => confirmButtonRef.current } }), []);
  const { modal, header, content, footer } = getStepConfig(step, { ...props, confirmButtonRef }) ?? {};

  useEffect(() => {
    fetchDepartments(email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  if (!modal) {
    return null;
  }

  return (
    <Modal
      key={modal.id}
      className={classnames('remote-access-dialog', modal.modifier)}
      label={modal.label}
      focusTrapProps={modal.shouldFocusPrimaryAction ? primaryActionFocusTrapProps : undefined}
      onClose={onClose}
      id={modal.id}>
      <div className="remote-access-modal__header">{header}</div>
      <div className="remote-access-modal__content">{content}</div>
      {footer && <div className="remote-access-modal__footer">{footer}</div>}
    </Modal>
  );
}

RemoteAccessDialog.propTypes = {
  step: PropTypes.string.isRequired,
  email: PropTypes.string,
  fetchDepartments: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

export default RemoteAccessDialog;
